import { CheckCircleTwoTone, DeleteOutlined, EditTwoTone, WarningTwoTone, InfoOutlined, ExclamationCircleOutlined, ToolOutlined, FilterOutlined, DownOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Space, Table, Typography, message, notification, Switch, Progress, Dropdown } from "antd";
import moment from "moment";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import axios from "axios";
import { T1 } from "../../appconfig/texts";
import Loading from "../../components/loading";
import EllipsisText from "../../components/text/EllipsisText";
import formatEmissions, { totalEmissions } from "../../components/text/resultEmissions";
import { findFormTexts, findSite, firstCompany, firstSite, openSites } from "../../helper/attribute";
import { exceldateformat, timeFormat } from "../../helper/time";
import { fixDecimals, intersection, removeIndexes, reverseObj } from "../../helper/wodash";
import { findForm } from "../../static/formRoutes";
import { getGHGTitle } from "../../static/ghg.static";
import { routePath } from "../../static/routes.static";
import InputDataPage from "../form/inputdata";
import DataForm from "../form";
import { Pair } from "../form/reviewpage";
import { isNumber } from "lodash";
import { morphResults } from "../results/resultsCalcs";
import { getCurrentPlan, planValue } from "../../helper/plans";
import { getFuzzyMatchedSiteId, getToDisplayValue, returnExcelOptions } from "./helper2";
import { getFuzzyMatchedOptions } from "./intermediate";
import { FaBandAid } from "react-icons/fa";
import { convertIATA } from "../../helper/airport";

let EDITROWINDEX = -1;
let MAXROWS = 2000;

// Configuration flag for fuzzy matching - set to false to disable fuzzy matching entirely
const ENABLE_FUZZY_MATCHING = false;

function arraysToObjects(keysArray, dataArrays) {
  return dataArrays.map((dataArray) => {
    return keysArray.reduce((obj, key, index) => {
      obj[key] = dataArray[index];
      return obj;
    }, {});
  });
}

function manageMetrics(row, companyMetrics) {
  let transformedRow = {};

  for (const [key, value] of Object.entries(row)) {
    if (key.startsWith("Metric:")) {
      const metricName = key.split(":")[1].trim();
      const metricIndex = companyMetrics.findIndex((metric) => metric.title === metricName);
      if (metricIndex !== -1) {
        transformedRow[`m_${metricIndex + 1}`] = value; // Index starts at 1
      } else {
        transformedRow[key] = value;
      }
    } else {
      transformedRow[key] = value;
    }
  }

  return transformedRow;
}

export default function ReviewPage({
  data,
  setData,
  errorRows,
  settings = { type: "Default" }, // IF TYPE IS MONTHLY, TRANSFORM THE SHEET
}) {
  const form = findForm(data.name);
  const [modalStates, setModalStates] = useState({ edit: false });
  const [modalValues, setModalValues] = useState({ edit: {} });
  const [displayInfoModal, setDisplayInfoModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [companyMetrics, setCompanyMetrics] = useState(firstCompany()?.inputMetrics?.filter((metric) => metric.enabled) ?? []);
  const [processing, setProcessing] = useState(true);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0, stage: '' });
  const [isAIFixEnabled, setIsAIFixEnabled] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100', '200'],
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });
  const navigate = useNavigate();

  // if ai is used
  const parsedContentRows = data["parsedContent"]?.query_result;
  const isAiParsedContent = Array.isArray(parsedContentRows);

  console.log("isAiParsedContent", isAiParsedContent, parsedContentRows);
  console.log(companyMetrics, firstCompany()?.inputMetrics);

  const file = data.file;
  const formFields = {};
  findFormTexts(data.name, "forms")?.forEach((obj) => (formFields[obj.name] = obj.title));

  useEffect(() => {
    try {
      InitProcedure();
    } catch (err) { }
  }, []);

  console.log(data);

  // Chunked processing function for heavy operations
  const processRowsInChunks = async (rows, processFunction, chunkSize = 30) => {
    const results = [];
    const total = rows.length;

    for (let i = 0; i < total; i += chunkSize) {
      const chunk = rows.slice(i, i + chunkSize);
      const chunkResults = await Promise.all(chunk.map(processFunction));
      results.push(...chunkResults);

      // Update progress
      setProcessingProgress({
        current: Math.min(i + chunkSize, total),
        total,
        stage: 'Processing rows'
      });

      // Yield control to the UI
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    return results;
  };

  // Chunked processing for fuzzy matching
  const processFuzzyMatchingInChunks = async (rows, form_fields, all_options_cache, chunkSize = 25) => {
    const total = rows.length;

    for (let i = 0; i < total; i += chunkSize) {
      const endIndex = Math.min(i + chunkSize, total);

      for (let rowIndex = i; rowIndex < endIndex; rowIndex++) {
        const row = rows[rowIndex];

        // Process fuzzy matching for this row
        try {
          for (let fieldx of form_fields) {
            const field = fieldx["name"];
            if (!row[field] || row[field] === "") continue;

            const options = all_options_cache[rowIndex]?.find((fieldy) => fieldy.key === field)?.excel_options;

            if (data.name === "electricity" && field === "q2") {
              continue;
            }

            if (options && options.length) {
              const matched_options = getFuzzyMatchedOptions(row[field], options);

              if (data.name === "flight") convertIATA(row);

              if (matched_options.length) {
                if (JSON.stringify(row[field]) !== JSON.stringify(matched_options[0].value)) {
                  row[field] += " [No match]";
                  row["notFoundError"] = true;
                } else {
                  row["notFoundError"] = false;
                }
              } else {
                row["notFoundError"] = true;
                row[field] += " [No match]";
              }
            }
          }
        } catch (err) {
          console.error(`Error processing row ${rowIndex + 1}:`, err);
        }
      }

      // Update progress
      setProcessingProgress({
        current: endIndex,
        total,
        stage: 'Fuzzy matching fields'
      });

      // Yield control to the UI
      await new Promise(resolve => setTimeout(resolve, 5));
    }
  };

  const InitProcedure = (
    autoFix = false,
    afterFn = () => {
      console.log("processing done...");
    }
  ) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    let rows = [],
      errorRows = [];
    reader.onload = async (event) => {
      try {
        setProcessingProgress({ current: 0, total: 0, stage: 'Reading file...' });

        rows = event.target.result;
        const workbook = XLSX.read(rows, { type: "array" });
        let sheetName = settings.sheetName;

        const sheet = workbook.Sheets[sheetName];
        let initialData = {};
        try {
          const config = workbook.Sheets["Config"];
          if (config) {
            const configdata = XLSX.utils.sheet_to_json(config, {
              header: 1,
              raw: true,
            });
            function arrayToObject(arr) {
              return arr.reduce((obj, [key, value]) => {
                obj[key] = value;
                return obj;
              }, {});
            }
            initialData = arrayToObject(configdata);
          }
        } catch (err) { }
        let fields = reverseObj(formFields);

        setProcessingProgress({ current: 0, total: 0, stage: 'Parsing Excel data...' });

        rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: null,
          blankrows: false,
        });

        console.log("rows", rows);

        if (isAiParsedContent) {
          rows = parsedContentRows;
        } else {
          if (rows?.length > MAXROWS) {
            // MAX LENGTH CHECK
            message.warning("Maximum number of rows are " + MAXROWS);
            rows = rows.slice(0, MAXROWS + 1);
          }
          data.excelRows = [...rows.slice(1, rows?.length)];
          rows[0] = rows[0].map((key) => {
            if (fields[key]) {
              return fields[key];
            } else {
              return key;
            }
          });
          rows = arraysToObjects(rows[0], rows.slice(1, rows?.length));
        }

        console.log("ROWS IN VIEW", rows);
        if (Array.isArray(rows)) {
          // add a unique row as well.
          rows = rows.map((row) => {
            if (data.name === "flight") {
              // TODO: delete after the template is fixed
              row.flight_class = row.flight_type.includes("Domestic") ? "Average passenger" : row.flight_class;
            }
            return { ...row, bulk_uploaded: true };
          });
        }

        if (settings.type === "Monthly") {
          setProcessingProgress({ current: 0, total: 0, stage: 'Processing monthly data...' });
          console.log("Applying monthly settings");
          // spread rows reading months
          const newRows = [];
          const months = Array.from({ length: 12 }, (_, index) => {
            return moment().month(index).format("MMM");
          });
          function getLastDayOfMonth(year, month) {
            const lastDayOfMonth = moment(`${year}-${month}-01`, "YYYY-MMM-DD").endOf("month").format(timeFormat);
            return lastDayOfMonth;
          }
          rows?.forEach((row) => {
            console.log(row);
            const year = row.Year;
            months?.forEach((month) => {
              const em = row[month];
              if (typeof em === "number") {
                console.log(month, em);
                const transformed_date = getLastDayOfMonth(year, month);
                const newRow = { ...row };
                newRow["date"] = transformed_date;
                const { amount_key } = form;
                newRow[amount_key ?? "amount"] = em; // main key is?
                newRows.push(newRow);
              }
            });
          });
          rows = newRows;
          console.log(rows);
        }

        if (!rows.length) rows = [];

        // Pre-calculate all options for all rows to avoid repeated calculations (only if fuzzy matching is enabled)
        let all_options_cache = [];

        if (ENABLE_FUZZY_MATCHING) {
          setProcessingProgress({ current: 0, total: rows.length, stage: 'Preparing field options...' });

          for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
            const row = rows[rowIndex];
            const all_options = await returnExcelOptions(form, { ...settings, ...row, ...initialData });
            all_options_cache[rowIndex] = all_options;

            // Process optionsAreKeyValue transformations
            all_options.forEach((field, index) => {
              if (field.optionsAreKeyValue && row.hasOwnProperty(field.key)) {
                const excel_options = field.excel_options;
                const matchingOption = excel_options.find((option) => option.key === row[field.key]);
                if (matchingOption) {
                  row[field.key] = matchingOption.value;
                }
              }
            });

            if (rowIndex % 10 === 0) {
              setProcessingProgress({
                current: rowIndex + 1,
                total: rows.length,
                stage: 'Preparing field options...'
              });
              await new Promise(resolve => setTimeout(resolve, 1));
            }
          }
        }

        // Basic row processing (site and date)
        setProcessingProgress({ current: 0, total: rows.length, stage: 'Processing basic row data...' });

        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
          const row = rows[rowIndex];

          // Add site processing
          const siteId = getFuzzyMatchedSiteId(row["Site"], openSites, firstSite);
          delete row["Site"];
          row.siteId = siteId;
          row.date = settings.type === "Monthly" ? row?.date : exceldateformat(row?.date);

          if (rowIndex % 25 === 0) {
            setProcessingProgress({
              current: rowIndex + 1,
              total: rows.length,
              stage: 'Processing basic row data...'
            });
            await new Promise(resolve => setTimeout(resolve, 1));
          }
        }

        // Process fuzzy matching in chunks (only if enabled and not autoFix)
        if (ENABLE_FUZZY_MATCHING && !autoFix) {
          const form_fields = findFormTexts(data.name, "forms");
          await processFuzzyMatchingInChunks(rows, form_fields, all_options_cache);
        }

        // Process rows with form.pack in chunks
        setProcessingProgress({ current: 0, total: rows.length, stage: 'Calculating results...' });

        const processRow = async (beforerow, index) => {
          let row = beforerow;
          let packed = row;
          try {
            if (row.date === "Invalid date") throw "Invalid Date Format";
            row = { ...row, ...initialData };
            row = manageMetrics(row, companyMetrics);
            packed = await form.pack({ ...row });
            if (isNaN(packed.result)) throw "Result not calculated";
          } catch (err) {
            packed = { ...row, err };
            errorRows.push(beforerow);
          }
          return packed;
        };

        rows = await processRowsInChunks(rows, processRow);
        rows = rows.map((row, i) => ({ ...row, key: Number(i + 1) + ")" }));

        // Final processing for error rows
        setProcessingProgress({ current: 0, total: rows.length, stage: 'Final processing...' });

        const finalProcessRow = async (row) => {
          try {
            if (row.err) {
              row = await form.pack(row, true);
              delete row.err;
              return row;
            }
          } catch (err) {
          } finally {
            return row;
          }
        };

        const finalRows = await processRowsInChunks(rows, finalProcessRow);

        afterFn();
        setData({
          ...data,
          rows: finalRows,
        });
      } catch (err) {
        setProcessing({ err });
        console.error("error in processing data", err);
      } finally {
        setProcessing(false);
        setProcessingProgress({ current: 0, total: 0, stage: '' });
      }
    };
  };

  const handleEdit = (record, index) => {
    //Edit rows before uploading
    // Find the actual index in data.rows using the unique key
    EDITROWINDEX = data?.rows.findIndex((row) => row.key === record.key);
    setModalValues({ ...modalValues, edit: record });
    setModalStates({ ...modalStates, edit: true });
  };

  const handleDisplayCompleteInfo = (_, index) => {
    setDisplayInfoModal(_);
  };

  const handleDeleteRows = (indexes) => {
    setSelectedRowKeys([]);
    const newData = {
      ...data,
      rows: removeIndexes(
        data.rows,
        indexes.map((i) => parseInt(i))
      ),
      excelRows: removeIndexes(
        data.excelRows,
        indexes.map((i) => parseInt(i))
      ),
    };
    setData(newData);
  };

  // Memoized filtered data for performance
  const filteredData = useMemo(() => {
    const morphedResults = morphResults(data?.rows);
    if (showErrorsOnly) {
      return morphedResults.filter(record => record.err || record.notFoundError || (!record.result && record.result !== 0));
    }
    return morphedResults;
  }, [data?.rows, showErrorsOnly]);

  const output_keys = planValue("onlyDisplayInputsInCombinedResults") ? findFormTexts(form.name, "forms") : findFormTexts(data.name, "tables", true);

  const columns = [
    {
      title: "No.",
      width: 70,
      render: (item, record) => {
        return item.key;
      },
    },
    {
      title: "Site",
      width: 130,
      render: (item, record, index) => {
        return findSite(record.siteId).title;
      },
    },
    ...output_keys.map((field) => {
      const obj = {
        title: field.title,
        width: 190,
        dataIndex: field.name,
        render: (value, record, index) => {
          const getActualValue = () => {
            const vd = getToDisplayValue(data.name, field.name, value, record);
            return (
              <div className={typeof record?.err?.split === "function" ? intersection(record?.err?.split(" "), obj.title?.split(" ")).length && "antd-cell-err" : ""}>
                {field.name.includes("ghg") ? vd : isNumber(vd) ? fixDecimals(vd) : vd}
              </div>
            );
          };
          return getActualValue();
        },
      };
      return obj;
    }),
    {
      title: "Result",
      dontDisplayIf: planValue("onlyDisplayInputsInCombinedResults"),
      width: 160,
      render: (_, record, index) => {
        if ((!record.result && record.result !== 0) || record.notFoundError)
          return (
            <small style={{ color: "red" }}>
              Result not calculated. Please{" "}
              <a onClick={() => handleEdit(record, index)} style={{ color: "red", textDecoration: "underline" }}>
                click here
              </a>
              .
            </small>
          );
        if ("result" in record && record.result !== NaN && record.result !== undefined) {
          return (
            <Space>
              {formatEmissions(record.result)}
              <CheckCircleTwoTone twoToneColor={"green"} />
            </Space>
          );
        } else {
          return (
            <Space>
              <WarningTwoTone twoToneColor={"red"} />
              <small>{record.err || "Calculation error! "}</small>
            </Space>
          );
        }
      },
    },
    {
      title: "Comment",
      width: 200,
      dataIndex: "comment",
      render: (_) => (
        <small>
          <EllipsisText maxLength={20} text={_} />
        </small>
      ),
    },
    {
      title: "Data Link",
      width: 140,
      dataIndex: "dataLink",
      render: (_) => <small>{_}</small>,
    },
    {
      title: "Data Tag",
      width: 140,
      dataIndex: "resulttag",
      render: (_) => <small>{_}</small>,
    },
    {
      title: "Cost",
      width: 140,
      dataIndex: "cost",
      render: (_) => <small>{_}</small>,
    },
    ...companyMetrics?.map((metric, index) => {
      const dataIndex = "m_" + (index + 1);
      return {
        title: metric.title,
        dataIndex,
        key: dataIndex,
        width: 140,
      };
    }),
    {
      title: "Options",
      fixed: "right",
      width: 150,
      render: (_, record, index) => {
        return (
          <Space>
            <Button size="small" type="primary" style={{ background: "grey" }} onClick={() => handleDisplayCompleteInfo(record, index)}>
              <InfoOutlined />
            </Button>
            <Button type="primary" size="small" onClick={() => handleEdit(record, index)}>
              <EditTwoTone twoToneColor={"white"} />
            </Button>
            <Button type="primary" size="small" style={{ background: "firebrick" }} onClick={() => {
              // Find the actual index in data.rows using the unique key
              const actualIndex = data?.rows.findIndex((row) => row.key === record.key);
              if (actualIndex !== -1) {
                handleDeleteRows([actualIndex]);
              }
            }}>
              <DeleteOutlined />
            </Button>
          </Space>
        );
      },
    },
  ].filter((x) => (x.dontDisplayIf ? false : true));

  const handleExport = (exportType = 'error') => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    let rows = [];
    let filename = "export.xlsx";

    // Determine which rows to export based on type
    switch (exportType) {
      case 'original':
        rows = data.excelRows || [];
        filename = "original_data.xlsx";
        break;
      case 'error':
        rows = data.excelRows?.filter((_, index) => data.rows[index]?.err || data.rows[index]?.notFoundError) || [];
        filename = "error_rows.xlsx";
        break;
      case 'selected':
        // Get selected row indices and map to original excel rows
        const selectedIndices = selectedRowKeys.map(key => {
          const rowIndex = data?.rows.findIndex(row => row.key === key);
          return rowIndex;
        }).filter(index => index !== -1);
        rows = data.excelRows?.filter((_, index) => selectedIndices.includes(index)) || [];
        filename = "selected_rows.xlsx";
        break;
      default:
        rows = data.excelRows?.filter((_, index) => data.rows[index]?.err || data.rows[index]?.notFoundError) || [];
        filename = "error_rows.xlsx";
    }

    console.log(rows, "ROWS BULK UPLOAD");

    if (rows.length === 0) {
      message.warning(`No ${exportType} rows to export`);
      return;
    }

    // Convert JSON to worksheet with proper headers
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    // Write the workbook to a buffer
    const buffer = XLSX.write(workbook, { type: "buffer", bookType: "xlsx" });

    // Create a Blob from the buffer
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a temporary anchor element
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;

    // Programmatically trigger the download
    link.click();

    // Clean up the temporary anchor element
    URL.revokeObjectURL(link.href);
  };

  if (processing)
    return (
      <Loading
        title={
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              Generating Data. This might take a while.
            </div>
            {processingProgress.total > 0 && (
              <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                  {processingProgress.stage}
                </div>
                <Progress
                  percent={Math.round((processingProgress.current / processingProgress.total) * 100)}
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  {processingProgress.current} / {processingProgress.total} rows processed
                </div>
              </div>
            )}
          </div>
        }
      />
    );
  if (processing.err) {
    return <Alert type="error" message={processing.err} />;
  }

  const info_output_keys = planValue("onlyDisplayInputsInCombinedResults") ? findFormTexts(form.name, "forms") : findFormTexts(form.name, "reviewpages", true);

  const handleAutoFix = async () => {
    Modal.confirm({
      title: "Start Auto-Correction?",
      content: isAIFixEnabled 
        ? "AI-powered auto-correction will analyze each error row individually and suggest fixes. This may take longer but provides more intelligent corrections."
        : "We're using fuzzy matching to help identify and fix any errors. This process ensures improved accuracy and faster results.",
      onOk: async () => {
        const button = document.querySelector("#autoFixButton"); // Select button by ID
        if (button) {
          // Change button content to show loading
          button.innerHTML = `<span> Loading...</span>`;
        }

        if(isAIFixEnabled){
          // Use AI fix for error rows
          try {
            const errorRowsData = data.rows.filter(row => row.err || row.notFoundError);
            
            if (errorRowsData.length === 0) {
              message.info("No error rows found to fix");
              if (button) {
                button.innerHTML = `Auto Fix Applied✅`;
                button.style = { ...button.style, backgroundColor: "lightgrey", borderColor: "darkgrey" };
                button.disabled = true;
              }
              return;
            }

            // Process each error row individually
            const fixedRows = [];
            let successCount = 0;
            let failCount = 0;

            for (let i = 0; i < errorRowsData.length; i++) {
              const errorRow = errorRowsData[i];
              
              try {
                // Update button to show progress
                if (button) {
                  button.innerHTML = `<span>Fixing row ${i + 1}/${errorRowsData.length}...</span>`;
                }

                // Prepare the error context and row data for AI
                const errorContext = {
                  error: errorRow.err || "Field validation error or no match found",
                  rowData: { ...errorRow },
                  formType: form.name,
                  formTitle: form.title
                };

                const instructions = `You are helping to fix data validation errors in a ${form.title} form. 
                The row has the following error: "${errorContext.error}".
                Please analyze the row data and suggest corrections. 
                Return ONLY a valid JSON object with the corrected field values. 
                Do not include explanations or additional text.
                Focus on fixing obvious data format issues, typos, or missing required fields.`;

                const query = `Fix this row data: ${JSON.stringify(errorContext.rowData)}`;

                const response = await axios.post('https://bot-alistermartin.pythonanywhere.com/simple_ai_query', {
                  instructions,
                  query
                });

                // Parse AI response
                let aiFixedData;
                try {
                  // Try to extract JSON from response - check multiple possible fields
                  const responseText = response.data.resp_text || response.data.response || response.data;
                  
                  if (typeof responseText === 'object') {
                    // If it's already an object, use it directly
                    aiFixedData = responseText;
                  } else {
                    // If it's a string, try to parse it as JSON
                    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                      aiFixedData = JSON.parse(jsonMatch[0]);
                    } else {
                      aiFixedData = JSON.parse(responseText);
                    }
                  }
                  
                  console.log("AI Fixed Data for row", i + 1, ":", aiFixedData);
                } catch (parseError) {
                  console.error("Failed to parse AI response for row", i + 1, parseError);
                  console.error("Raw response:", response.data);
                  failCount++;
                  continue;
                }

                // Clean AI response - remove fields that shouldn't be overwritten
                const cleanedAiData = { ...aiFixedData };
                delete cleanedAiData.err;
                delete cleanedAiData.key;
                delete cleanedAiData.bulk_uploaded;
                delete cleanedAiData.siteId; // Don't let AI change the site
                
                // Merge AI fixes with original row data
                const mergedRowData = {
                  ...errorRow,
                  ...cleanedAiData,
                  notFoundError: false
                };

                // Remove error flags
                delete mergedRowData.err;

                // Try to pack the fixed data
                const packedData = await form.pack(mergedRowData);
                
                if (!isNaN(packedData.result)) {
                  fixedRows.push({
                    originalIndex: data.rows.findIndex(row => row.key === errorRow.key),
                    fixedData: packedData
                  });
                  successCount++;
                } else {
                  failCount++;
                }

              } catch (error) {
                console.error(`Failed to fix row ${i + 1}:`, error);
                failCount++;
              }

              // Small delay to prevent overwhelming the API
              await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Apply all successful fixes
            if (fixedRows.length > 0) {
              const newRows = [...data.rows];
              fixedRows.forEach(({ originalIndex, fixedData }) => {
                if (originalIndex !== -1) {
                  newRows[originalIndex] = {
                    ...fixedData,
                    key: data.rows[originalIndex].key // Preserve original key
                  };
                }
              });

              setData({
                ...data,
                rows: newRows
              });
            }

            // Update button with results
            if (button) {
              if (successCount > 0) {
                button.innerHTML = `AI Fixed ${successCount} rows ✅`;
                button.style = { ...button.style, backgroundColor: "lightgreen", borderColor: "darkgreen" };
                message.success(`AI successfully fixed ${successCount} out of ${errorRowsData.length} error rows`);
              } else {
                button.innerHTML = `AI Fix Failed ❌`;
                button.style = { ...button.style, backgroundColor: "lightcoral", borderColor: "darkred" };
                message.error("AI could not fix any rows. Please try manual fixes.");
              }
              
              if (failCount > 0) {
                button.insertAdjacentHTML(
                  "afterend",
                  `<div id="ai-fix-message" style="color: orange; margin-top: 10px; font-size: 14px; font-weight: 500;">${failCount} rows could not be fixed automatically. Please fix them manually using the edit option.</div>`
                );
              }
              
              button.disabled = true;
            }

          } catch (error) {
            console.error("AI Fix error:", error);
            message.error("AI fix service is currently unavailable. Please try manual fixes.");
            if (button) {
              button.innerHTML = `AI Fix Failed ❌`;
              button.style = { ...button.style, backgroundColor: "lightcoral", borderColor: "darkred" };
              button.disabled = true;
            }
          }
          return;
        }

        // Call InitProcedure (synchronous)
        InitProcedure(true, () => {
          // Restore original button content after InitProcedure completes
          if (button) {
            if (errorRows()) {
              button.insertAdjacentHTML(
                "afterend",
                `<div id="error-message" style="color: red; margin-top: 10px; font-size: 14px; font-weight: 500;">Some errors are still left. Please fix them individually using the edit option.</div>`
              );
            }
            button.innerHTML = `Auto Fix Applied✅`;
            button.style = { ...button.style, backgroundColor: "lightgrey", borderColor: "darkgrey" };
            button.disabled = true;
          }
        });
      },
    });
  };

  return (
    <div>
      <Typography.Title level={3}>{form.title} Bulk Upload</Typography.Title>
      <Typography.Text>Please review data below before uploading.</Typography.Text>
      {errorRows() ? (
        <Alert
          style={{
            marginBottom: "16px",
            padding: "20px",
          }}
          showIcon
          icon={<ExclamationCircleOutlined style={{ color: "#D93025" }} />}
          type="error"
          message={
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {errorRows() + " out of " + data?.rows?.length + " rows have errors."}
              <Button
                type="primary"
                onClick={async () => await handleAutoFix()}
                id="autoFixButton"
                style={{
                  backgroundColor: "#FF4D4F",
                  borderColor: "#FF4D4F",
                  borderRadius: "5px",
                  fontWeight: "600",
                  padding: "0 12px",
                  fontSize: "0.9rem",
                }}
                icon={<ToolOutlined />}
              >
                Auto-Fix Errors
              </Button>
            </div>
          }
          description={<span style={{ fontSize: "0.85rem", fontWeight: "500" }}>Please correct them before uploading.</span>}
        />
      ) : null}

      {data.zeroEmissionsError ? <Alert type="warning" showIcon message="Zero Emissions" description="Total emissions are calculated to be zero. Please recheck the table before uploading. " /> : ""}

      <Table
        rowKey={(record, i) => record.key || (i + "-row")}
        pagination={{
          ...pagination,
          total: filteredData.length,
          onChange: (page, pageSize) => {
            setPagination(prev => ({
              ...prev,
              current: page,
              pageSize: pageSize
            }));
          },
          onShowSizeChange: (current, size) => {
            setPagination(prev => ({
              ...prev,
              current: 1,
              pageSize: size
            }));
          }
        }}
        title={() => (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                ℹ️ {totalEmissions(data?.rows)}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <FilterOutlined />
                  <span>Show errors only:</span>
                  <Switch
                    checked={showErrorsOnly}
                    onChange={setShowErrorsOnly}
                    size="small"
                  />
                </div>
                <div style={{ display: "none", alignItems: "center", gap: "8px" }}>
                  <span>🤖 AI Fix:</span>
                  <Switch
                    checked={isAIFixEnabled}
                    onChange={setIsAIFixEnabled}
                    size="small"
                  />
                </div>
              </div>
            </div>
            <Space>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'original',
                      label: 'Export Original Data',
                      onClick: () => handleExport('original'),
                    },
                    {
                      key: 'error',
                      label: 'Export Error Rows',
                      onClick: () => handleExport('error'),
                    },
                    ...(selectedRowKeys.length > 0 ? [{
                      key: 'selected',
                      label: `Export Selected Rows (${selectedRowKeys.length})`,
                      onClick: () => handleExport('selected'),
                    }] : []),
                  ],
                }}
                trigger={['click']}
              >
                <Button type="primary">
                  Export
                </Button>
              </Dropdown>
              {selectedRowKeys.length ? (
                <Button type="primary" style={{ background: "firebrick" }} onClick={() => {
                  // Convert selected keys to actual indices in data.rows
                  const actualIndices = selectedRowKeys.map(key => {
                    return data?.rows.findIndex(row => row.key === key);
                  }).filter(index => index !== -1);
                  handleDeleteRows(actualIndices);
                }}>
                  Delete {selectedRowKeys.length} rows
                </Button>
              ) : null}
            </Space>
          </div>
        )}
        dataSource={filteredData}
        rowClassName={(record, index) => (record.err ? "antd-table-row-red" : "")}
        columns={columns}
        rowSelection={{
          selectedRowKeys: selectedRowKeys,
          type: "checkbox",
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            console.log("Selected row keys:", selectedRowKeys);
          },
        }}
        scroll={{
          x: 1200,
        }}
      />

      {/**MODALS */}
      <Modal
        destroyOnClose
        title={"Edit Row"}
        width={1400}
        okText="Update"
        open={modalStates.edit}
        onCancel={() => setModalStates({ ...modalStates, edit: false })}
        onOk={async () => {
          try {
            if (EDITROWINDEX === -1) {
              message.error("Row not found for editing");
              return;
            }

            // Pack the edited data and update the row
            const packedData = await form.pack({ ...modalValues.edit, notFoundError: false });

            // Create a new data object to trigger re-render
            const newRows = [...data.rows];
            newRows[EDITROWINDEX] = {
              ...packedData,
              key: data.rows[EDITROWINDEX].key, // Preserve the original key
            };

            // Remove error flag
            delete newRows[EDITROWINDEX]["err"];
            delete newRows[EDITROWINDEX]["notFoundError"];

            const newData = {
              ...data,
              rows: newRows
            };
            setData(newData);

            setModalStates({ ...modalStates, edit: false });
            message.success("Row updated successfully");
          } catch (err) {
            console.error("Error updating row:", err);
            message.error(typeof err === 'string' ? err : "Failed to update row");
          }
        }}
      >
        <>
          <InputDataPage
            data={{ ...modalValues.edit, name: form.name, siteId: modalValues.edit.siteId || data.siteId }}
            setData={(newData) => setModalValues({ ...modalValues, edit: newData })}
            isBulkPage={true}
            bulkPageErr={modalValues.edit.err}
          />
        </>
      </Modal>
      {displayInfoModal && (
        <Modal open={displayInfoModal} onCancel={() => setDisplayInfoModal(false)} onOk={() => setDisplayInfoModal(false)}>
          <>
            <Typography.Title level={3}>Complete information</Typography.Title>
            {info_output_keys.map((textObj, i) => (
              <Pair
                key={i + "pair"}
                name={textObj.name}
                option={textObj.title}
                value={textObj.name.includes("ghg") ? getGHGTitle(displayInfoModal[textObj.name]) : displayInfoModal[textObj.name]}
                formname={displayInfoModal.name}
                description={textObj.desc}
              />
            ))}
          </>
        </Modal>
      )}
    </div>
  );
}
